import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Copy,
  Delta,
  Kbd,
  Modal,
  Moon,
  Price,
  Rocket,
  Search,
  Sun,
  TokenMark,
  Wallet,
  useTheme,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import type { DemoToken } from '@/lib/demo/types';
import { compact, weiToNumber } from '@/lib/format';
import { useMarket } from '@/providers/market';
import { useCommandPalette } from './AppShell';

const MAX_TOKEN_RESULTS = 8;
const TRENDING_COUNT = 6;

/* ────────────────────────────────────────────────────────────────────────────
   Matching.

   A palette is judged on its first row, so this ranks rather than filters. Tiers
   run strongest-first and only the tier decides order; volume breaks ties. The
   loose tiers are deliberately fenced off: a bare subsequence test matches
   r…a…t scattered across "Feral Hog Market", which reads as a broken search and
   buries the real hit.
   ──────────────────────────────────────────────────────────────────────────── */

const TIER = {
  /** Symbol is exactly the query. */
  exact: 0,
  /** Symbol starts with the query. */
  symbolPrefix: 1,
  /** The name, or any word in it, starts with the query. */
  namePrefix: 2,
  /** Query appears as a contiguous run anywhere. */
  contains: 3,
  /** Characters appear in order and close together — typo tolerance only. */
  loose: 4,
} as const;

/** Below this length only the prefix tiers run, so one letter can't return everything. */
const MIN_LOOSE_QUERY = 2;
/** Tier 4 is a fallback: it only surfaces when the precise tiers came up thin. */
const PRECISE_FLOOR = 3;

const WORD_SPLIT = /[^a-z0-9]+/i;

function hasWordPrefix(haystack: string, needle: string): boolean {
  return haystack.split(WORD_SPLIT).some((word) => word.startsWith(needle));
}

/**
 * Subsequence match with a gap budget. Unbounded gaps are what let "rat" reach
 * "Feral Hog Market"; keeping the run compact preserves real typo tolerance
 * ("pepfl" → "PEPEFLOOR") and drops the noise.
 */
function isTightSubsequence(haystack: string, needle: string): boolean {
  const budget = Math.max(2, needle.length);
  let cursor = 0;
  let gaps = 0;
  let last = -1;
  for (const ch of needle) {
    const found = haystack.indexOf(ch, cursor);
    if (found < 0) return false;
    if (last >= 0) gaps += found - last - 1;
    if (gaps > budget) return false;
    last = found;
    cursor = found + 1;
  }
  return true;
}

/**
 * `null` when nothing matches. `short` is the symbol-like field (exact/prefix
 * tiers), `long` the prose one. `loose` opens tiers 3–4.
 */
function matchTier(
  short: string,
  long: string,
  needle: string,
  opts: { loose: boolean; subsequence: boolean },
): number | null {
  const s = short.toLowerCase();
  const l = long.toLowerCase();
  if (s === needle) return TIER.exact;
  if (s.startsWith(needle)) return TIER.symbolPrefix;
  if (l.startsWith(needle) || hasWordPrefix(l, needle)) return TIER.namePrefix;
  if (!opts.loose) return null;
  if (s.includes(needle) || l.includes(needle)) return TIER.contains;
  if (!opts.subsequence) return null;
  if (isTightSubsequence(s, needle) || isTightSubsequence(l, needle)) return TIER.loose;
  return null;
}

interface ActionItem {
  id: string;
  label: string;
  hint: string;
  icon: ReactNode;
  run: () => void;
}

type Row =
  | { key: string; kind: 'token'; token: DemoToken }
  | { key: string; kind: 'action'; action: ActionItem };

export function CommandPalette() {
  const { open, closePalette } = useCommandPalette();
  // Explicit annotation keeps the ranking maths typed against the data contract.
  const allTokens: DemoToken[] = useMarket().tokens;
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { push } = useToast();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const actions = useMemo<ActionItem[]>(
    () => [
      {
        id: 'launch',
        label: 'Launch a token',
        hint: 'Open the launch form',
        icon: <Rocket size={17} />,
        run: () => navigate('/launch'),
      },
      {
        id: 'portfolio',
        label: 'View portfolio',
        hint: 'Positions, floor value and P&L',
        icon: <Wallet size={17} />,
        run: () => navigate('/portfolio'),
      },
      {
        id: 'theme',
        label: 'Toggle theme',
        hint: 'Switch between dark and light',
        icon: theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />,
        run: () => toggle(),
      },
      {
        id: 'copy-url',
        label: 'Copy current URL',
        hint: 'Share this view',
        icon: <Copy size={17} />,
        run: () => {
          void navigator.clipboard
            ?.writeText(window.location.href)
            .then(() => push({ tone: 'success', title: 'Link copied' }))
            .catch(() => push({ tone: 'error', title: 'Could not copy link' }));
        },
      },
    ],
    [navigate, toggle, push, theme],
  );

  const trimmed = query.trim();

  const tokenResults = useMemo(() => {
    if (!trimmed) {
      return [...allTokens].sort((a, b) => b.volume24hEth - a.volume24hEth).slice(0, TRENDING_COUNT);
    }
    const needle = trimmed.toLowerCase();
    const loose = needle.length >= MIN_LOOSE_QUERY;

    const ranked = allTokens
      .map((token) => ({
        token,
        tier: matchTier(token.symbol, token.name, needle, { loose, subsequence: true }),
      }))
      .filter((entry): entry is { token: DemoToken; tier: number } => entry.tier !== null)
      .sort((a, b) => a.tier - b.tier || b.token.volume24hEth - a.token.volume24hEth);

    // Typo tolerance shouldn't outrank real answers, so the loose tier only joins
    // the list when the precise tiers left it nearly empty.
    const precise = ranked.filter((entry) => entry.tier < TIER.loose);
    const pool = precise.length >= PRECISE_FLOOR ? precise : ranked;
    return pool.slice(0, MAX_TOKEN_RESULTS).map((entry) => entry.token);
  }, [allTokens, trimmed]);

  const actionResults = useMemo(() => {
    if (!trimmed) return actions;
    const needle = trimmed.toLowerCase();
    const loose = needle.length >= MIN_LOOSE_QUERY;
    return actions
      .map((action) => ({
        action,
        tier: matchTier(action.label, action.label, needle, { loose, subsequence: false }),
      }))
      .filter((entry): entry is { action: ActionItem; tier: number } => entry.tier !== null)
      .sort((a, b) => a.tier - b.tier)
      .map((entry) => entry.action);
  }, [actions, trimmed]);

  const rows = useMemo<Row[]>(
    () => [
      ...tokenResults.map<Row>((token) => ({ key: `token:${token.address}`, kind: 'token', token })),
      ...actionResults.map<Row>((action) => ({
        key: `action:${action.id}`,
        kind: 'action',
        action,
      })),
    ],
    [tokenResults, actionResults],
  );

  // Reset per opening, and keep the cursor inside the (shrinking) result list.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => setActiveIndex(0), [trimmed]);

  useEffect(() => {
    if (activeIndex > rows.length - 1) setActiveIndex(rows.length ? rows.length - 1 : 0);
  }, [rows.length, activeIndex]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, rows.length]);

  const select = useCallback(
    (row: Row) => {
      closePalette();
      if (row.kind === 'token') navigate(`/token/${row.token.address}`);
      else row.action.run();
    },
    [closePalette, navigate],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (rows.length ? (i + 1) % rows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) select(row);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePalette();
    }
  };

  const rowClass = (active: boolean) =>
    cn(
      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors duration-150 ease-swift',
      active ? 'bg-elevated' : 'hover:bg-elevated/70',
    );

  return (
    <Modal open={open} onClose={closePalette} size="lg">
      {/* pr-8 leaves room for the Modal's own close button, which floats top-right. */}
      <div role="search" className="flex items-center gap-2.5 border-b border-line pb-3 pr-8">
        <Search size={18} className="shrink-0 text-subtle" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Search tokens and commands"
          aria-controls="command-palette-results"
          placeholder="Search tokens or run a command…"
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-fg outline-none placeholder:text-subtle focus:ring-0"
        />
        <span className="shrink-0 font-mono text-2xs text-subtle">
          {rows.length} {rows.length === 1 ? 'result' : 'results'}
        </span>
      </div>

      <div id="command-palette-results" className="-mx-1 max-h-[min(60vh,26rem)] overflow-y-auto px-1 py-2">
        {rows.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-muted">
            Nothing matches “{trimmed}”.
          </p>
        ) : (
          <>
            {tokenResults.length > 0 && (
              <p className="px-2.5 pb-1.5 pt-1 text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
                {trimmed ? 'Tokens' : 'Trending'}
              </p>
            )}
            <ul role="list" className="space-y-0.5">
              {rows.map((row, index) => {
                const active = index === activeIndex;
                const isFirstAction = row.kind === 'action' && index === tokenResults.length;
                return (
                  <li key={row.key}>
                    {isFirstAction && (
                      <p className="px-2.5 pb-1.5 pt-3 text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
                        Actions
                      </p>
                    )}
                    <button
                      type="button"
                      ref={(node) => {
                        rowRefs.current[index] = node;
                      }}
                      onMouseMove={() => setActiveIndex(index)}
                      onClick={() => select(row)}
                      className={rowClass(active)}
                    >
                      {row.kind === 'token' ? (
                        <>
                          <TokenMark symbol={row.token.symbol} hue={row.token.hue} glyph={row.token.glyph} size="sm" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-fg">
                              {row.token.symbol}
                            </span>
                            <span className="block truncate text-2xs text-subtle">
                              {row.token.name} · vol {compact(row.token.volume24hEth)} ETH
                            </span>
                          </span>
                          <span className="shrink-0 text-right">
                            <span className="block text-sm text-fg">
                              <Price value={weiToNumber(row.token.spotPrice)} />
                            </span>
                            <span className="block text-2xs">
                              <Delta value={row.token.change24h} />
                            </span>
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            aria-hidden="true"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-elevated text-muted"
                          >
                            {row.action.icon}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-fg">
                              {row.action.label}
                            </span>
                            <span className="block truncate text-2xs text-subtle">
                              {row.action.hint}
                            </span>
                          </span>
                          <span aria-hidden="true" className="shrink-0 text-2xs text-subtle">
                            ↵
                          </span>
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line pt-3 text-2xs text-subtle">
        <span className="flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span className="flex items-center gap-1.5">
          <Kbd>↵</Kbd> open
        </span>
        <span className="flex items-center gap-1.5">
          <Kbd>esc</Kbd> close
        </span>
      </div>
    </Modal>
  );
}

export default CommandPalette;
